/**
 * The minimum a column must describe for this arithmetic to apply.
 *
 * Structural rather than a concrete DTO (B6.9e): the SE composer's
 * `ReportSectionColumn` and the MSE designer's `ReportTemplateColumn` are
 * different API shapes that both satisfy it, so the resize maths and the
 * `SectionWidthEditor` are shared instead of duplicated. Every function below is
 * generic in the column type and returns the caller's own type, so no field is
 * dropped on the way through and neither caller has to widen its model.
 *
 * `minWidthFraction` is always the SERVER's value — per column on the composer
 * side, per section on the designer side. It is never computed in the browser.
 */
export interface ResizableColumn {
  position: number;
  widthFraction: number;
  /** EXCLUSIVE lower bound: a width must stay strictly above this. */
  minWidthFraction: number;
}

/**
 * Column-width arithmetic for the visual resizer (B6.5a-3).
 *
 * Kept pure and separate from the component so the invariants below can be
 * tested directly rather than inferred from rendered output.
 *
 * The governing rule: dragging the divider between columns i and i+1 moves width
 * BETWEEN THOSE TWO ONLY. Their combined total is preserved exactly — the right
 * column is derived by subtraction from the pair total rather than computed
 * independently — so the section's overall total stays invariant by
 * construction. There is no renormalisation step and therefore no drift, which
 * is what keeps an untouched section bit-identical to its template.
 */

/**
 * The smallest increment that reliably produces a different double.
 *
 * The API's `minWidthFraction` is an EXCLUSIVE bound — the server rejects a
 * width that merely equals it, because at exactly that value the renderer has
 * zero drawable space. Clamping to the bound itself would therefore hand the
 * user a 400 at the extreme of a drag, so the clamp stops one representable
 * step inside it.
 */
function justAbove(x: number): number {
  return x + Math.max(Math.abs(x), Number.MIN_VALUE) * Number.EPSILON;
}

function justBelow(x: number): number {
  return x - Math.max(Math.abs(x), Number.MIN_VALUE) * Number.EPSILON;
}

/** The legal range for the LEFT column of a divider, given both minimums. */
export function leftBounds(
  left: ResizableColumn,
  right: ResizableColumn,
): { min: number; max: number } {
  const pairTotal = left.widthFraction + right.widthFraction;
  return {
    min: justAbove(left.minWidthFraction),
    max: justBelow(pairTotal - right.minWidthFraction),
  };
}

/**
 * Applies a width delta at the divider after `index`, returning a new column
 * array. Every column other than the two adjacent to the divider is returned
 * unchanged, by identity.
 *
 * `delta` is a fraction of the section width — positive widens the left column.
 * A delta that would push either column to or past its minimum is clamped, so a
 * drag simply stops at the legal edge instead of being rejected.
 */
export function resizeAt<T extends ResizableColumn>(
  columns: T[],
  index: number,
  delta: number,
): T[] {
  const left = columns[index];
  const right = columns[index + 1];
  if (!left || !right || !Number.isFinite(delta)) return columns;

  const pairTotal = left.widthFraction + right.widthFraction;
  const { min, max } = leftBounds(left, right);
  if (min >= max) return columns; // no legal room to move at all

  const nextLeft = Math.min(Math.max(left.widthFraction + delta, min), max);
  if (nextLeft === left.widthFraction) return columns;

  // Derived by subtraction, never computed independently: this is what makes
  // the pair total — and therefore the section total — exactly invariant.
  const nextRight = pairTotal - nextLeft;

  return columns.map((c, i) => {
    if (i === index) return { ...c, widthFraction: nextLeft };
    if (i === index + 1) return { ...c, widthFraction: nextRight };
    return c;
  });
}

/**
 * Two width lists are the same when every fraction matches exactly.
 *
 * Exact comparison is deliberate. The server stores an override only when a
 * width differs from the template, so a dirty check that tolerated "close
 * enough" would mark an untouched report as edited — and one that rounded would
 * hide a real edit.
 */
export function sameWidths(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((w, i) => w === b[i]);
}

/**
 * Fractions of a section's columns, in column order.
 *
 * Takes the narrowest shape that answers the question — a width — rather than a
 * full `ResizableColumn`. The designer's `ReportTemplateColumn` carries no
 * per-column minimum (the API reports one per SECTION), and requiring one here
 * would force a projection at every call site for a value this function never
 * reads.
 */
export function widthsOf(columns: readonly { widthFraction: number }[]): number[] {
  return columns.map((c) => c.widthFraction);
}
